import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Card, CardContent, CircularProgress, Divider, Grid, List, ListItem, ListItemAvatar, Avatar, ListItemText, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { useCart } from './contexts/CartContext.jsx';
import { createPaypalOrder, capturePaypalOrder, getPaypalConfig } from './api';
import { getPrimaryImageUrl } from './utils/productImage';

const loadPaypalScript = (clientId, currency) => {
  const src = `https://www.paypal.com/sdk/js?client-id=${clientId}&currency=${currency || 'USD'}&components=buttons`;

  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = resolve;
    script.onerror = reject;
    document.body.appendChild(script);
  });
};

const formatPrice = cents => `$${((cents || 0) / 100).toFixed(2)}`;

const PaypalCheckout = () => {
  const { cart, cartToken, loading, refresh } = useCart();
  const [paypalConfig, setPaypalConfig] = useState(null);
  const [scriptReady, setScriptReady] = useState(false);
  const [error, setError] = useState(null);
  const buttonRef = useRef(null);
  const navigate = useNavigate();
  const buttonRenderedRef = useRef(false);

  useEffect(() => {
    (async () => {
      try {
        const cfg = await getPaypalConfig();
        if (!cfg?.client_id) {
          throw new Error('Missing PayPal credentials');
        }
        setPaypalConfig(cfg);
        await loadPaypalScript(cfg.client_id, cfg.currency || cart?.currency || 'USD');
        setScriptReady(true);
      } catch (err) {
        console.error(err);
        setError('Unable to load PayPal. Please refresh the page or try again later.');
      }
    })();
  }, [cart?.currency]);

  useEffect(() => {
    if (!scriptReady || !window.paypal || !cartToken || !buttonRef.current || buttonRenderedRef.current) {
      return;
    }

    const buttons = window.paypal.Buttons({
      style: {
        layout: 'vertical',
        shape: 'rect',
        color: 'gold',
        label: 'paypal'
      },
      createOrder: async () => {
        setError(null);
        try {
          const order = await createPaypalOrder(cartToken);
          if (!order?.id) {
            throw new Error('PayPal order missing id');
          }
          return order.id;
        } catch (err) {
          console.error(err);
          setError('We could not start the PayPal checkout. Please try again.');
          throw err;
        }
      },
      onApprove: async data => {
        try {
          await capturePaypalOrder(data.orderID, cartToken);
          await refresh();
          navigate('/confirmation');
        } catch (err) {
          console.error(err);
          setError('We were unable to capture the payment. Please contact support.');
        }
      },
      onError: err => {
        console.error(err);
        setError('PayPal reported an error. Please try again or contact support.');
      }
    });

    buttons.render(buttonRef.current);
    buttonRenderedRef.current = true;

    return () => {
      buttonRenderedRef.current = false;
    };
  }, [scriptReady, cartToken, refresh, navigate]);

  if (loading) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!cart || (cart.items || []).length === 0) {
    return (
      <Alert severity="info">Your cart is empty. Head back to the catalog to add a cloth.</Alert>
    );
  }

  return (
    <Grid container spacing={4} alignItems="flex-start">
      <Grid item xs={12} md={6}>
        <Typography variant="h5" sx={{ mb: 2 }}>Order Summary</Typography>
        <Card sx={{ borderRadius: 3 }}>
          <CardContent>
            <List dense>
              {cart.items.map(item => (
                <ListItem key={item.id} alignItems="flex-start" sx={{ mb: 1 }}>
                  <ListItemAvatar>
                    <Avatar variant="rounded" src={getPrimaryImageUrl(item.product)} alt={item.product?.name} />
                  </ListItemAvatar>
                  <ListItemText
                    primary={item.product?.name}
                    secondary={
                      <>
                        <Typography component="span" variant="body2" color="text.primary">
                          Qty {item.quantity}
                        </Typography>
                        <Typography variant="body2">
                          {formatPrice(item.price_cents)}
                        </Typography>
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="h6">Total</Typography>
              <Typography variant="h6">{formatPrice(cart.totals?.subtotal_cents)}</Typography>
            </Box>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={6}>
        <Typography variant="h5" sx={{ mb: 2 }}>Pay with PayPal</Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Box ref={buttonRef} sx={{ minHeight: 45 }} />
        {!scriptReady && !error && (
          <Box sx={{ mt: 2 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" sx={{ ml: 2 }} component="span">
              Loading PayPal…
            </Typography>
          </Box>
        )}
      </Grid>
    </Grid>
  );
};

export default PaypalCheckout;
